import { compilePublicHead } from "../../seo/index.js";
export { buildSitemapIndexXml, buildSitemapXml, buildSitemapXmlFromEntries, } from "../../sitemap/index.js";
const DefaultDomOptions = {
    rootElementId: "app",
    stateGlobalName: "__PUBLIC_SITE_STATE__",
};
const DefaultTemplateMarkers = {
    head: "<!--public-site-head-->",
    state: "<!--public-site-state-->",
    app: "<!--public-site-app-->",
};
export function escapeHtml(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
function escapeAttribute(value) {
    return escapeHtml(value).replace(/'/g, "&#39;");
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
export function serializeJsonForHtmlScript(value) {
    return JSON.stringify(value)
        .replace(/</g, "\\u003C")
        .replace(/>/g, "\\u003E")
        .replace(/&/g, "\\u0026")
        .replace(/\u2028/g, "\\u2028")
        .replace(/\u2029/g, "\\u2029");
}
function resolveDomOptions(options = {}) {
    return {
        rootElementId: options.rootElementId ?? DefaultDomOptions.rootElementId,
        stateGlobalName: options.stateGlobalName ?? DefaultDomOptions.stateGlobalName,
    };
}
export function hasPublicTemplateMarkers(html) {
    return Object.values(DefaultTemplateMarkers).every((marker) => html.includes(marker));
}
function assertTemplateMarker(html, marker) {
    if (!html.includes(marker))
        throw new Error(`Missing public site template marker: ${marker}`);
}
function renderPublicHeadNode(node) {
    switch (node.kind) {
        case "title":
            return `<title data-public-head="true" data-public-head-key="${node.key}">${escapeHtml(node.content)}</title>`;
        case "meta":
            if ("name" in node)
                return `<meta data-public-head="true" data-public-head-key="${node.key}" name="${escapeAttribute(node.name)}" content="${escapeAttribute(node.content)}">`;
            return `<meta data-public-head="true" data-public-head-key="${node.key}" property="${escapeAttribute(node.property)}" content="${escapeAttribute(node.content)}">`;
        case "link": {
            const attributes = [
                "data-public-head=\"true\"",
                `data-public-head-key="${escapeAttribute(node.key)}"`,
                `rel="${escapeAttribute(node.rel)}"`,
                node.hreflang ? `hreflang="${escapeAttribute(node.hreflang)}"` : null,
                node.type ? `type="${escapeAttribute(node.type)}"` : null,
                `href="${escapeAttribute(node.href)}"`,
            ].filter(Boolean);
            return `<link ${attributes.join(" ")}>`;
        }
        case "structured-data":
            return `<script data-public-head="true" data-public-head-key="${escapeAttribute(node.key)}" type="${escapeAttribute(node.type)}">${serializeJsonForHtmlScript(node.json)}</script>`;
    }
}
export function renderPublicHeadTags(head) {
    return compilePublicHead(head)
        .map(renderPublicHeadNode)
        .join("\n    ");
}
function setHtmlLang(html, htmlLang) {
    const escapedLang = escapeAttribute(htmlLang);
    if (!/<html\b[^>]*>/i.test(html))
        throw new Error("Missing <html> element in public site shell.");
    return html.replace(/<html\b([^>]*)>/i, (tag, attributes) => {
        if (/\blang=(["']).*?\1/i.test(tag))
            return tag.replace(/\blang=(["']).*?\1/i, `lang="${escapedLang}"`);
        return `<html${attributes} lang="${escapedLang}">`;
    });
}
function markRootPrerendered(html, domOptions = {}) {
    const dom = resolveDomOptions(domOptions);
    const prerendered = domOptions.prerendered ?? true;
    if (!prerendered)
        return html;
    const rootRegex = new RegExp(`(<[a-zA-Z][^>]*\\bid=(["'])${escapeRegExp(dom.rootElementId)}\\2[^>]*>)`, "i");
    const match = html.match(rootRegex);
    if (!match?.[1])
        throw new Error(`Missing public site root element: ${dom.rootElementId}`);
    const rootTag = match[1];
    if (!/\bdata-prerendered=(["'])false\1/i.test(rootTag))
        throw new Error(`Missing public site root attribute contract: data-prerendered="false"`);
    const nextRootTag = rootTag.replace(/\bdata-prerendered=(["'])false\1/i, "data-prerendered=\"true\"");
    return html.replace(rootTag, nextRootTag);
}
export function injectPublicHead(html, head) {
    assertTemplateMarker(html, DefaultTemplateMarkers.head);
    return setHtmlLang(html, head.htmlLang)
        .replace(DefaultTemplateMarkers.head, renderPublicHeadTags(head));
}
export function injectPublicAppState(html, state, domOptions = {}) {
    const dom = resolveDomOptions(domOptions);
    const appStateScript = `<script>window.${dom.stateGlobalName}=${serializeJsonForHtmlScript(state)};</script>`;
    assertTemplateMarker(html, DefaultTemplateMarkers.state);
    return html.replace(DefaultTemplateMarkers.state, appStateScript);
}
export function injectPublicAppHtml(html, appHtml, domOptions = {}) {
    assertTemplateMarker(html, DefaultTemplateMarkers.app);
    return markRootPrerendered(html, domOptions).replace(DefaultTemplateMarkers.app, appHtml);
}
export function renderPublicHtmlDocument(options) {
    return injectPublicAppState(injectPublicAppHtml(injectPublicHead(options.shell, options.head), options.appHtml, options.dom), options.state, options.dom);
}
export function createPublicPageHtml(options) {
    return renderPublicHtmlDocument({
        shell: options.shell,
        appHtml: options.appHtml,
        head: options.head,
        state: options.state,
        dom: options.dom,
    });
}
export function createPublicNotFoundHtml(options) {
    return createPublicPageHtml(options);
}
//# sourceMappingURL=index.js.map